import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination as MuiPagination,
} from "@mui/material";
import { ReportDetailDialog } from "./ReportDetailDialog";

export function EcosTab({ eco }) {
  const hasPartialRange = Boolean(eco?.from) !== Boolean(eco?.to);

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography fontWeight={600} mb={2}>
          Filtros
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Fecha desde"
            type="date"
            value={eco?.from}
            onChange={(e) => eco?.setFrom(e?.target?.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="Fecha hasta"
            type="date"
            value={eco?.to}
            onChange={(e) => eco?.setTo(e?.target?.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />

          <Button variant="contained" onClick={eco?.search}>
            Buscar
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              eco?.setFrom("");
              eco?.setTo("");
              eco?.resetPage();
            }}
          >
            Limpiar
          </Button>
        </Stack>

        {hasPartialRange && (
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            Completá “desde” y “hasta” o dejá ambos vacíos.
          </Typography>
        )}

        {!hasPartialRange && (
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            Si no elegís fechas, se muestran los últimos reportes (página 1).
          </Typography>
        )}
      </Paper>

      {eco?.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {eco?.error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        {eco?.loading ? (
          <Box p={3} display="flex" alignItems="center" gap={2}>
            <CircularProgress size={22} />
            <Typography>Cargando reportes de ecos…</Typography>
          </Box>
        ) : eco?.rows?.length === 0 ? (
          <Box p={3}>
            <Typography fontWeight={600}>Sin reportes</Typography>
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Tipo de reporte</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Reportó</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {eco?.rows?.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.reportType}
                        color={
                          r.reportType === "Post" ? "primary" : "secondary"
                        }
                      />
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {r.id}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip size="small" label={r.type || "-"} />
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={r.status || "pending"}
                        color={r.status === "pending" ? "warning" : "default"}
                      />
                    </TableCell>

                    <TableCell>
                      {r.createdAt
                        ? new Date(r.createdAt).toLocaleString("es-AR")
                        : "-"}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {r.reporter?.name || "N/A"} {r.reporter?.lastname || ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.reporter?.email || `ID: ${r.reporterId}`}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {r.description || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={async () => {
                          console.log("Report clicked:", r.reportType);
                          if (r.reportType === "Comentario") {
                            const details = await eco.reportCommentDetails(
                              r.commentId
                            );

                            eco?.setSelected({
                              ...details,
                              reportId: r.id,
                              reportType: "Comentario",
                              commentId: r.commentId,
                            });
                            return;
                          }

                          const details = await eco.reportPostDetails(r.postId);

                          eco?.setSelected({
                            ...details,
                            reportId: r.id,
                            reportType: "Post",
                            postId: r.postId,
                          });
                        }}
                      >
                        Ver detalle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider />

            <Box
              p={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                Página {eco?.page} de {eco?.totalPages}
              </Typography>

              <MuiPagination
                page={eco?.page}
                count={eco?.totalPages}
                onChange={(_, p) => eco?.setPage(p)}
              />
            </Box>
          </>
        )}
      </Paper>

      <ReportDetailDialog
        open={Boolean(eco?.selected)}
        onDelete={async (reportId, typeFromDialog) => {
          if (!reportId) return;

          const type = typeFromDialog ?? eco?.selected?.reportType;

          await eco?.deleteEcoPostOrCommentReport?.(reportId, type);
          eco?.setSelected(null);
          await eco?.fetchNow?.();
        }}
        onClose={() => eco?.setSelected(null)}
        title="Detalle del reporte"
        subtitle={eco?.selected?.reportType}
        payload={eco?.selected}
      />
    </>
  );
}
